import { FC, Fragment, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
// components
import SubProductTypeForm from './SubProductTypeForm';
import { Alert } from 'components/common/Alert';
import { BackdropLoader } from 'components/common/BackdropLoader';
// Schema, styles, graphql, constants
import {
  ADD_TEXT,
  HTTP_STATUS,
  productSubTypeInitialValue,
  SUB_PRODUCT_TYPE_IS_CREATING_TEXT,
  ADD_SUB_PRODUCT_TYPE_TEXT,
} from 'constants/index';
import { AddSubProductTypeFormType } from 'interfaces';
import { addSubProductTypeSchema } from 'validationSchema';
import { useCreateSubProductTypeMutation } from 'generated/graphql';
import { flexCenterBetween } from 'styles/commonComponentStyle';

const Add: FC = (): JSX.Element => {
  const navigate = useNavigate();

  const methods = useForm<AddSubProductTypeFormType>({
    defaultValues: productSubTypeInitialValue,
    resolver: yupResolver(addSubProductTypeSchema),
  });

  const { handleSubmit } = methods;

  const [createSubProductType, { loading: createLoading }] = useCreateSubProductTypeMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { createSubProductType } = data;
      const { response } = createSubProductType || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.CREATED) {
        navigate(-1);
        Alert.success(message || '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const onSubmit: SubmitHandler<AddSubProductTypeFormType> = async (data) => {
    const { name, productType } = data || {};
    const { value } = productType || {};

    await createSubProductType({
      variables: {
        createSubProductTypeInput: {
          name,
          productTypeId: value,
        },
      },
    });
  };

  const loading = createLoading;

  return (
    <Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={flexCenterBetween}>
            <Typography variant="h5">{ADD_SUB_PRODUCT_TYPE_TEXT}</Typography>
            <Button variant="contained" color="primary" type="submit" disabled={loading}>
              {ADD_TEXT}
            </Button>
          </Box>
          <SubProductTypeForm loading={loading} />
        </form>
      </FormProvider>
      <BackdropLoader open={loading} text={SUB_PRODUCT_TYPE_IS_CREATING_TEXT} />
    </Fragment>
  );
};

export default Add;
